import { SiteBatchUpdateEntityToBatchUpdateDto } from "src/app/features/Site/services/mappers/SiteBatchUpdateEntityToBatchUpdateDto";
import type { SiteBatchUpdateEntity } from "src/app/features/Site/types/SiteBatchUpdateEntity";
import type { ImportResult } from "../../../../../components/FeatureButtons/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { SiteGroupCopyEntity } from "../../types/SiteGroupCopyEntity";
import type { SiteGroupEntity } from "../../types/SiteGroupEntity";
import { SiteGroupCopyEntityToCopyDto } from "../mappers/SiteGroupCopyEntityToCopyDto";
import { SiteGroupDtoToEntity } from "../mappers/SiteGroupDtoToEntity";
import { SiteGroupEntityToDto } from "../mappers/SiteGroupEntityToDto";

const SiteGroupApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        SiteGroupLoad: builder.query<SiteGroupEntity, FormQuery>({
            providesTags: [ResourceName.SITE_GROUPS],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITE_GROUPS}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return SiteGroupDtoToEntity(response);
            },
        }),
        SiteGroupSave: builder.mutation<SiteGroupEntity, FormMutation<SiteGroupEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITE_GROUPS}`;
                const method = "POST";

                const body = SiteGroupEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.SITE_GROUPS],
        }),
        SiteGroupUpdate: builder.mutation<SiteGroupEntity, FormMutation<SiteGroupEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITE_GROUPS}/${mutation.data.id}`;
                const method = "PUT";

                const body = SiteGroupEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.SITE_GROUPS],
        }),
        SiteGroupCopy: builder.mutation<SiteGroupEntity, FormMutation<SiteGroupCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITE_GROUPS}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = SiteGroupCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.SITE_GROUPS],
        }),
        SiteGroupBatchUpdate: builder.mutation<ImportResult, FormMutation<SiteBatchUpdateEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.SITE_GROUPS}/${FORM_ACTION.BATCH_UPDATE}`;
                const method = "PUT";

                const body = SiteBatchUpdateEntityToBatchUpdateDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            transformResponse: (response: { successCount?: number; failedCount?: number; errors?: string[] }): ImportResult => ({
                importSuccessCount: response.successCount,
                importFailedCount: response.failedCount,
                importErrors: response.errors,
            }),
            invalidatesTags: ["mainCache"],
        }),
    }),
});

export const {
    useSiteGroupLoadQuery,
    useSiteGroupSaveMutation,
    useSiteGroupUpdateMutation,
    useSiteGroupCopyMutation,
    useSiteGroupBatchUpdateMutation,
} = SiteGroupApi;
